import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'font-poppins inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-30 [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:shrink-0 cursor-pointer',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90',
        destructive:
          'border-4 border-destructive text-destructive-foreground shadow-xs hover:bg-destructive/10',
        outline:
          'border border-input bg-primary-dark-o-1 shadow-xs hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost:
          'hover:bg-sidebar hover:text-accent-foreground text-primary-dark font-bold rounded-[10px] focus-visible:ring-0 focus-visible:bg-sidebar focus-visible:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        bordered: 'font-bold text-sm border-primary border',
        toogle: 'text-primary border-primary border',
        enrich:
          'flex gap-2 transition-all duration-300 ease-in-out [&_svg]:size-auto rounded-lg border-4 border-primary-persian-green bg-white shadow-[0px_1px_7px_1px_rgba(76,69,53,0.21)] font-poppins text-sm font-bold leading-none text-foreground',
        enirch_primary: 'py-2! bg-white w-full text-foreground text-base font-bold leading-none',
        enrich_secondary: 'bg-primary-dark-o-1 border-none text-sm font-normal',
        enrich_dialog:
          'bg-transparent w-full text-foreground text-base font-bold leading-none border border-primary-aqua-green',
        enrich_menu_outline:
          'bg-transparent text-white text-[15px] font-bold leading-none border border-white [&_svg]:size-4 gap-1.5 px-8! h-auto!',
        enrich_menu:
          'bg-transparent text-white text-[15px] font-bold leading-none [&_svg]:size-4 gap-1.5 border-none justify-start! pl-0! py-1.5! h-auto! rounded-none',
      },
      size: {
        default: 'h-14 px-8 py-3',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-lg px-8',
        icon: 'h-9 w-9 [&_svg]:size-6 p-2',
        iconSmall: '[&_svg]:size-5 px-2 py-2',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    if (variant === 'enirch_primary') {
      return (
        <div
          className={cn(
            'inline-block rounded-md bg-gradient-to-r from-[#0CA5A2] to-[#5CD7A3] p-1',
            { 'border-primary-persian-green border bg-white p-0': props.disabled },
            className,
          )}
        >
          <Comp className={cn(buttonVariants({ variant, size }))} ref={ref} {...props} />
        </div>
      );
    }
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
